import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { Trans } from "react-i18next";
import Typography from "@mui/material/Typography";
import TimeDuration from "../../../components/TimeDuration";
import Close from "../../../assets/icons/Close.svg";
import OpenCall from "../../../assets/icons/OpenCall.svg";
import CloseCall from "../../../assets/icons/CloseCall.svg";
import EstablishCall from "../../../assets/icons/EstablishCall.svg";
import RingCall from "../../../assets/icons/RingCall.svg";
import ErrorIcon from "../../../assets/icons/Error.svg";
import { anim, modalIn, modalUp } from "../../../components/Animations/utils";

const IconComponent: {
  [key in TeamsCallStatus]: React.FunctionComponent<
    React.SVGAttributes<SVGElement>
  >;
} = {
  connecting: EstablishCall,
  ringing: RingCall,
  connected: OpenCall,
  disconnected: CloseCall,
  noAnswer: CloseCall,
  error: ErrorIcon,
};

const StatusIcon = ({ status }: { status: TeamsCallStatus }) => {
  const StatusIconComponent = useMemo(() => IconComponent[status], [status]);
  return <StatusIconComponent width="6.4rem" height="6.4rem" />;
};

interface TeamsCallModalProps {
  status?: TeamsCallStatus;
  onClose: () => void;
  onEndCall: () => void;
  willNavigateToAccessControl?: boolean;
}

const TeamsCallModal = ({
  status,
  onEndCall,
  onClose,
  willNavigateToAccessControl,
}: TeamsCallModalProps) => {
  const statusKey = useMemo(() => {
    if (willNavigateToAccessControl && status === "disconnected") {
      return "disconnectedAccess";
    }
    return status as TeamsCallStatus;
  }, [willNavigateToAccessControl, status]);

  if (!status) {
    return null;
  }

  return (
    <div className="main-modal">
      <motion.div {...anim(modalIn)} className="main-modal__background" />
      <motion.div
        {...anim(modalUp)}
        className="main-modal__content text-center"
      >
        <Typography variant="h1" className={`text-${status} mt-5`}>
          <Trans i18nKey={`teamsCall:status.${statusKey}.title`} />
        </Typography>
        {status === "connected" && (
          <Typography variant="h2" style={{ height: "7rem" }}>
            <TimeDuration />
          </Typography>
        )}
        <div className="main-modal__icon-box">
          <div className="m-auto call">
            <StatusIcon status={status} />
          </div>
        </div>
        {status !== "disconnected" &&
          status !== "error" &&
          status !== "noAnswer" && (
            <button
              className="display-6 py-4 px-5 text-white bg-secondary fs-medium rounded"
              onClick={onEndCall}
            >
              <Trans i18nKey="teamsCall:controls.endCall" />
            </button>
          )}
        {(status === "disconnected" ||
          status === "error" ||
          status === "noAnswer") && (
          <div className="main-modal__close" onClick={onClose}>
            <Close />
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default TeamsCallModal;
